// Společný důvod bloku — rozhodnutí zadavatele 31. 10. 2026 (docs/01, CLAUDE.md „Vysvětluj u stavu…“): když tentýž
// důvod brání více tlačítkům nebo řádkům najednou (typicky stav celé zakázky: „Zakázka je připravená k fakturaci —
// položky už se nemění…“), ukáže se JEDNOU nad blokem, kde to jde i s akcí, která blok uvolní (např. „Vrátit
// k opravě“ — jen pro toho, kdo na ni má právo). Neaktivní tlačítka uvnitř bloku (`TlacitkoSDuvodem`) ten text
// neopakují: zůstanou neaktivní, důvod mají v `title` a čtečka ho přečte přes `aria-describedby` odkazem na ten jeden
// text nahoře. Důvod, který platí jen pro jeden řádek (chybí váha, vážení na uloženém lístku…), dál stojí u řádku —
// stručně v jedné řádce vedle tlačítek (`vRadku`).
//
// Použití:
//   <BlokSeSpolecnymDuvodem duvod={duvodStavu(seznam.muze.pridat)}>
//     <SpolecnyDuvod akce={…} />          ← ten jeden text (bez něj se v tlačítkách nic neschová)
//     …tlačítka a tabulka…
//   </BlokSeSpolecnymDuvodem>
// Blok může nést i víc různých důvodů najednou (`duvod={[…]}`, `spolecneDuvodyStavu`) — ukážou se v jednom pruhu
// za sebou a tlačítko schová ten svůj, je‑li mezi nimi (kompaktní tabulky, zadání 3. 11. 2026).
// Bloky jde vnořit (blok tabulky → blok řádku); tlačítko hledá shodný důvod od nejbližšího bloku ven. Tlačítko schová
// svůj důvod jen tehdy, když je text bloku opravdu vykreslený (`SpolecnyDuvod` se přihlásí), takže zapomenutý
// `SpolecnyDuvod` důvod neztratí. Okna nad blokem (Modal, Zásuvka) blok přeruší — ukazují své důvody celé.
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import './komponenty.css';

interface KontextBloku {
  /** `id` vykresleného textu důvodu (cíl `aria-describedby`). */
  id: string;
  /** Text společného důvodu (víc důvodů za sebou); `null` = blok teď důvod nemá. */
  text: string | null;
  /** Jednotlivé důvody bloku — tlačítko se podřídí, je‑li jeho důvod mezi nimi. */
  duvody: readonly string[];
  /** Je text důvodu opravdu vykreslený (`SpolecnyDuvod` v bloku)? */
  ukazano: boolean;
  prihlasit: () => () => void;
  /** Nadřazený blok (vnoření: blok řádku v bloku tabulky). */
  rodic: KontextBloku | null;
}

const Kontext = createContext<KontextBloku | null>(null);

/** Blok, jehož tlačítka sdílejí jeden důvod; `duvod` prázdný = blok nic neschovává. */
export function BlokSeSpolecnymDuvodem({
  duvod,
  children,
}: {
  duvod: string | readonly string[] | null | undefined;
  children: ReactNode;
}) {
  const rodic = useContext(Kontext);
  const id = useId();
  const [pocet, setPocet] = useState(0);
  const prihlasit = useCallback(() => {
    setPocet((n) => n + 1);
    return () => setPocet((n) => n - 1);
  }, []);
  const seznam = (typeof duvod === 'string' ? [duvod] : (duvod ?? [])).filter((d) => d.trim());
  const klic = seznam.join('\u0000');
  const duvody = useMemo(() => (klic ? klic.split('\u0000') : []), [klic]);
  const text = duvody.length > 0 ? duvody.join(' ') : null;
  const hodnota = useMemo<KontextBloku>(
    () => ({ id, text, duvody, ukazano: pocet > 0, prihlasit, rodic }),
    [id, text, duvody, pocet, prihlasit, rodic],
  );
  return <Kontext.Provider value={hodnota}>{children}</Kontext.Provider>;
}

/** Okno nad blokem (Modal, Zásuvka): důvody uvnitř se ukazují celé, blok pod oknem je neschová. */
export function BezSpolecnehoDuvodu({ children }: { children: ReactNode }) {
  return <Kontext.Provider value={null}>{children}</Kontext.Provider>;
}

/**
 * Vykreslený společný důvod, kterému se tlačítko s důvodem `duvod` může podřídit (nejbližší blok se stejným textem),
 * jinak `null`. Pro `TlacitkoSDuvodem` a komponenty, které důvod kreslí samy.
 */
export function useSpolecnyDuvod(duvod: string | undefined): { id: string; text: string } | null {
  const blok = useContext(Kontext);
  if (!duvod) return null;
  for (let b = blok; b; b = b.rodic) {
    if (b.ukazano && b.duvody.includes(duvod)) return { id: b.id, text: duvod };
  }
  return null;
}

interface Props {
  /** Akce, která blok uvolní (např. „Vrátit k opravě“) — jen pro účet, který na ni má právo. */
  akce?: ReactNode;
  /** V řádku tabulky: krátce v jedné řádce vedle tlačítek (celý text v `title`). Výchozí je pruh nad blokem. */
  vRadku?: boolean;
  className?: string;
  'data-testid'?: string;
}

/** Ten jeden text společného důvodu bloku (nic, když blok důvod nemá). */
export function SpolecnyDuvod({
  akce,
  vRadku = false,
  className,
  'data-testid': testId = 'spolecny-duvod',
}: Props) {
  const blok = useContext(Kontext);
  const text = blok?.text ?? null;
  const prihlasit = blok?.prihlasit;
  // Přihlásit se před vykreslením do prohlížeče, ať tlačítka neblikla s důvodem navíc.
  useLayoutEffect(() => (text && prihlasit ? prihlasit() : undefined), [text, prihlasit]);
  if (!blok || !text) return null;
  if (vRadku) {
    return (
      <span
        id={blok.id}
        className={`duvod spolecny-duvod--radek${className ? ` ${className}` : ''}`}
        title={text}
        data-testid={testId}
      >
        {text}
      </span>
    );
  }
  return (
    <div
      className={`spolecny-duvod${className ? ` ${className}` : ''}`}
      role="note"
      data-testid={testId}
    >
      <p id={blok.id} className="spolecny-duvod__text">
        {text}
      </p>
      {akce && <span className="spolecny-duvod__akce">{akce}</span>}
    </div>
  );
}
